import { LayoutGrid, List } from 'lucide-react'
import { useSearchParams, type SetURLSearchParams } from 'react-router-dom'

export type ShopSortMode = 'featured' | 'price-asc' | 'price-desc' | 'newest'
export type ShopViewMode = 'grid' | 'list'

const SORT_OPTIONS: { value: ShopSortMode; label: string }[] = [
  { value: 'featured', label: 'Populaire' },
  { value: 'price-asc', label: 'Prix ↑' },
  { value: 'price-desc', label: 'Prix ↓' },
  { value: 'newest', label: 'Récent' },
]

export function parseShopSortParam(raw: string | null): ShopSortMode {
  if (raw === 'price-asc' || raw === 'price-desc' || raw === 'newest' || raw === 'featured') return raw
  return 'featured'
}

export function parseShopViewParam(raw: string | null): ShopViewMode {
  return raw === 'list' ? 'list' : 'grid'
}

export function ShopSortToolbar({
  searchParams,
  setSearchParams,
}: {
  searchParams?: URLSearchParams
  setSearchParams?: SetURLSearchParams
}) {
  const [hookParams, hookSet] = useSearchParams()
  const sp = searchParams ?? hookParams
  const patch = setSearchParams ?? hookSet

  const sort = parseShopSortParam(sp.get('sort'))
  const view = parseShopViewParam(sp.get('view'))

  const update = (mutate: (p: URLSearchParams) => void) => {
    patch(
      (prev) => {
        const next = new URLSearchParams(prev)
        mutate(next)
        return next
      },
      { replace: true }
    )
  }

  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tri</span>
        <div className="flex flex-wrap gap-1.5">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                update((p) => {
                  if (opt.value === 'featured') p.delete('sort')
                  else p.set('sort', opt.value)
                })
              }
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                sort === opt.value
                  ? 'bg-foreground text-white shadow-sm'
                  : 'bg-muted text-foreground hover:bg-muted'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Affichage</span>
        <div className="inline-flex rounded-full border border-border bg-muted p-0.5">
          <button
            type="button"
            aria-label="Grille"
            onClick={() => update((p) => p.delete('view'))}
            className={`rounded-full p-2 transition ${view === 'grid' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Liste"
            onClick={() => update((p) => p.set('view', 'list'))}
            className={`rounded-full p-2 transition ${view === 'list' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
